import {
  FormCategoryDTO,
  FormCategoryRuleDTO,
} from 'dtos';
import {
  FormFieldAssignee,
} from 'dtos/formFieldAssignee';
import {
  PdfTemplateField,
} from 'views/EMR/Settings/PdfTemplateBuilder/types';
import {
  getFieldAssignee,
} from 'views/EMR/Settings/PdfTemplateBuilder/utils/fieldAssigneeStyles';

export const ALL_FORM_FIELD_ASSIGNEES = [
  FormFieldAssignee.Patient,
  FormFieldAssignee.Clinician,
] as const;

type CategoryRuleLike = Pick<
  FormCategoryRuleDTO,
  'allowedAssignees' | 'defaultAssigneeForNewField'
> | null | undefined;

export function findFormCategoryRule(
  rules: FormCategoryRuleDTO[] | null | undefined,
  category: FormCategoryDTO | null | undefined,
): FormCategoryRuleDTO | null {
  return rules?.find(
    (rule) => rule.category?.value === category?.value,
  ) ?? null;
}

export function isMixedAssigneeMode(selection: FormFieldAssignee[]): boolean {
  return selection.length > 1;
}

export function getAllowedAssignees(categoryRule: CategoryRuleLike): FormFieldAssignee[] {
  const allowedAssignees = categoryRule?.allowedAssignees ?? ALL_FORM_FIELD_ASSIGNEES;

  return ALL_FORM_FIELD_ASSIGNEES.filter((assignee) => allowedAssignees.includes(assignee));
}

export function normalizeAssigneeSelection(
  selection: FormFieldAssignee[],
  categoryRule: CategoryRuleLike,
): FormFieldAssignee[] {
  const allowedAssignees = getAllowedAssignees(categoryRule);
  const normalizedSelection = ALL_FORM_FIELD_ASSIGNEES.filter(
    (assignee) => selection.includes(assignee) && allowedAssignees.includes(assignee),
  );

  if (normalizedSelection.length > 0) {
    return normalizedSelection;
  }

  const fallbackAssignee = categoryRule?.defaultAssigneeForNewField ?? FormFieldAssignee.Patient;

  if (allowedAssignees.includes(fallbackAssignee)) {
    return [fallbackAssignee];
  }

  return [allowedAssignees[0] ?? FormFieldAssignee.Patient];
}

export function inferFormAssigneeSelection(
  fields: PdfTemplateField[],
  categoryRule: CategoryRuleLike,
): FormFieldAssignee[] {
  const allowedAssignees = getAllowedAssignees(categoryRule);

  if (allowedAssignees.length <= 1) {
    return allowedAssignees.length > 0
      ? [allowedAssignees[0]]
      : [categoryRule?.defaultAssigneeForNewField ?? FormFieldAssignee.Patient];
  }

  const uniqueAssignees = Array.from(
    new Set(fields.map((field) => getFieldAssignee(field))),
  ).filter((assignee) => allowedAssignees.includes(assignee));

  if (uniqueAssignees.length > 1) {
    return normalizeAssigneeSelection(uniqueAssignees, categoryRule);
  }

  if (uniqueAssignees.length === 1) {
    return [uniqueAssignees[0]];
  }

  return normalizeAssigneeSelection(
    [categoryRule?.defaultAssigneeForNewField ?? FormFieldAssignee.Patient],
    categoryRule,
  );
}

export function getDefaultAssigneeForNewField(
  selection: FormFieldAssignee[],
  categoryRule: CategoryRuleLike,
): FormFieldAssignee {
  if (selection.length === 1) {
    return selection[0];
  }

  return categoryRule?.defaultAssigneeForNewField ?? FormFieldAssignee.Patient;
}

export function countFieldsWithAssignee(
  fields: PdfTemplateField[],
  assignee: FormFieldAssignee,
): number {
  return fields.filter((field) => getFieldAssignee(field) === assignee).length;
}

export function getCoercionTargetAssignee(
  nextSelection: FormFieldAssignee[],
): FormFieldAssignee | null {
  return nextSelection.length === 1 ? nextSelection[0] : null;
}

export function countFieldsNeedingCoercion(
  fields: PdfTemplateField[],
  nextSelection: FormFieldAssignee[],
): number {
  const coercionTarget = getCoercionTargetAssignee(nextSelection);

  if (coercionTarget === null) {
    return 0;
  }

  return fields.filter((field) => getFieldAssignee(field) !== coercionTarget).length;
}

export function getNextAssigneeSelection(
  currentSelection: FormFieldAssignee[],
  toggledAssignee: FormFieldAssignee,
  categoryRule: CategoryRuleLike,
): FormFieldAssignee[] | null {
  const allowedAssignees = getAllowedAssignees(categoryRule);

  if (!allowedAssignees.includes(toggledAssignee)) {
    return null;
  }

  const isSelected = currentSelection.includes(toggledAssignee);

  if (isSelected) {
    if (currentSelection.length === 1) {
      return null;
    }

    return normalizeAssigneeSelection(
      currentSelection.filter((assignee) => assignee !== toggledAssignee),
      categoryRule,
    );
  }

  return normalizeAssigneeSelection(
    [...currentSelection, toggledAssignee],
    categoryRule,
  );
}

export function getAssigneeLabel(assignee: FormFieldAssignee): string {
  return assignee === FormFieldAssignee.Clinician ? 'Clinician' : 'Patient';
}

export function getAssigneeModeLabel(selection: FormFieldAssignee[]): string {
  if (selection.length > 1) {
    return 'Mixed';
  }

  return `${getAssigneeLabel(selection[0])} only`;
}
